import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Loader2, ExternalLink, DollarSign, Clock, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight
} from 'lucide-react';

export default function AdminPaymentHistory() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const limit = 20;

  useEffect(() => {
    fetchPayments();
  }, [page, statusFilter]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      let url = `${API}/billing/admin/payments?page=${page}&limit=${limit}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const { data } = await axios.get(url, { headers });
      setPayments(data.data || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  const StatusBadge = ({ status }) => {
    const styles = {
      paid: 'bg-emerald-500/20 text-emerald-400',
      pending: 'bg-amber-500/20 text-amber-400',
      failed: 'bg-red-500/20 text-red-400',
      confirmed: 'bg-emerald-500/20 text-emerald-400',
    };
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>{status?.toUpperCase()}</span>;
  };

  return (
    <AdminLayout title="Payment History">
      <div className="space-y-6" data-testid="admin-payment-history">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Payment History</h1>
            <p className="text-sm text-muted-foreground mt-1">All merchant billing payments via CoinPayUp gateway</p>
          </div>
          <div className="flex items-center gap-2">
            {['', 'pending', 'paid', 'failed'].map(s => (
              <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                data-testid={`filter-${s || 'all'}`}>
                {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
              </Button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Payments</p>
                <p className="text-xl font-bold text-foreground">{total}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Paid</p>
                <p className="text-xl font-bold text-foreground">{payments.filter(p => p.status === 'paid').length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-xl font-bold text-foreground">{payments.filter(p => p.status === 'pending').length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No payments found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-payments-table">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-xs bg-muted/30">
                      <th className="text-left py-3 px-4 font-medium">Date</th>
                      <th className="text-left py-3 px-4 font-medium">Merchant</th>
                      <th className="text-left py-3 px-4 font-medium">Plan</th>
                      <th className="text-left py-3 px-4 font-medium">Amount</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Gateway Status</th>
                      <th className="text-left py-3 px-4 font-medium">Checkout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`admin-payment-${p.id}`}>
                        <td className="py-3 px-4 text-foreground text-xs">{new Date(p.created_at).toLocaleString()}</td>
                        <td className="py-3 px-4 text-foreground text-xs">{p.merchant_email || p.merchant_id?.slice(0, 8)}</td>
                        <td className="py-3 px-4 text-foreground">{p.plan_name || '-'}</td>
                        <td className="py-3 px-4 text-foreground font-semibold">${p.amount?.toFixed(2)} {p.currency}</td>
                        <td className="py-3 px-4"><StatusBadge status={p.status} /></td>
                        <td className="py-3 px-4"><StatusBadge status={p.gateway_status} /></td>
                        <td className="py-3 px-4">
                          {p.checkout_url && (
                            <a href={p.checkout_url} target="_blank" rel="noopener noreferrer"
                              className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 text-xs">
                              View <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} total)</p>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-8 w-8" disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" className="h-8 w-8" disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
